import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useContext, useRef, useState } from 'react';
import styled from 'styled-components';
import { paymentMethodsCacheKey, usePaymentMethodsDeletion } from '@syf-api-library/payment-methods';
import Button from 'syf-component-library/ui/atoms/Button';
import useWindowSize from 'syf-component-library/ui/customHooks/useWindowWidth';
import { ModalFooter } from 'syf-component-library/ui/patterns/SlideInModal';
import { FooterContainer } from 'syf-component-library/ui/patterns/SlideInModal/subcomponents';
import { Inline, Inset } from 'syf-component-library/ui/spacing';
import { DATA_ANALYTICS, DATA_OBJECT, DATA_REASON, DATA_TYPE, PAGE_FUNCTION, PAGE_NAME, SUB_FUNCTION } from '../../../const/analytics';
import { VALIDATION_ERROR_CODE } from '../../../const/errorCode';
import MainContext from '../../../Context';
import { getPaymentMethodErrorMessage } from '../../../helpers/getPaymentMethodErrorMessage';
import isPaymentMethodErrorCode from '../../../helpers/isPaymentMethodErrorCode';
import useApiMachine from '../../../hooks/machines/useApiMachine';
import usePaymentMethodMachine from '../../../hooks/machines/usePaymentMethodMachine';
import { useApiConfig } from '../../../hooks/useApiConfig';
import { t } from '../../../strings/i18n';
import AddPaymentMethodIcon from '../../atoms/PaymentMethod/AddPaymentMethodIcon';
import PaymentMethodsDesktop from '../../molecules/PaymentMethods/PaymentMethodsDesktop';
import PaymentMethodsMobile from '../../molecules/PaymentMethods/PaymentMethodsMobile';
import { AddPaymentMethod } from './AddPaymentMethod';
const AddPaymentMethodButtonWrapper = styled(Inset) `
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0px 0px 22px -12px rgb(0 0 0 / 75%);
`;
const PaymentMethodsWrapper = styled.div `
  flex-grow: 1;
  * {
    box-sizing: border-box;
  }
`;
const AddPaymentMethodButton = styled(Button) `
  text-transform: uppercase;
`;
export const ManagePaymentMethod = () => {
    const windowWidth = useWindowSize();
    const [apiMachine] = useApiMachine();
    const apiConfig = useApiConfig();
    const { sendToMainMachine, triggerAnalyticsEvent } = useContext(MainContext);
    const deletePaymentMethod = usePaymentMethodsDeletion(apiConfig);
    const { paymentMethodsQueryResult } = apiMachine.context;
    const { data: paymentMethodData } = paymentMethodsQueryResult;
    const paymentMethods = paymentMethodData === null || paymentMethodData === void 0 ? void 0 : paymentMethodData.paymentMethods;
    const [paymentMethodMachineState, sendToPaymentMethodMachine] = usePaymentMethodMachine();
    const [expandedId, setExpandedId] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const buttonRefs = useRef({});
    const handleClickAddPaymentMethod = () => sendToPaymentMethodMachine({ type: 'ADD_PAYMENT_METHOD' });
    if (paymentMethodMachineState.matches('addPaymentMethod')) {
        return _jsx(AddPaymentMethod, {});
    }
    const handleDeleteButton = (id) => {
        // Expand the row
        setExpandedId(id);
        setErrorMessage(null);
        triggerAnalyticsEvent({
            pageFunction: PAGE_FUNCTION.MAKE_PAYMENT.PAYMENTS,
            pageSubFunction: SUB_FUNCTION.MANAGE_PAYMENT_METHOD.REMOVE_PAYMENT_METHOD,
            pageName: PAGE_NAME.MANAGE_PAYMENT_METHOD.CONFIRMATION
        });
        // Focus the `Remove Payment Method` heading element after expanding
        setTimeout(() => {
            var _a;
            (_a = buttonRefs.current[`remove-heading-${id}`]) === null || _a === void 0 ? void 0 : _a.focus();
        });
    };
    const handleClosePrompt = () => {
        // Collapse the row
        setExpandedId(null);
        // Set the focus back to `Remove` button
        setTimeout(() => {
            var _a;
            (_a = buttonRefs.current[`remove-${expandedId}`]) === null || _a === void 0 ? void 0 : _a.focus();
        });
    };
    const handleSubmit = () => {
        handleClickAddPaymentMethod();
    };
    const handleCancel = () => {
        sendToMainMachine({
            type: 'GO_TO_PAYMENT_FLOW'
        });
    };
    const handleConfirmDeletePaymentMethod = async (paymentMethod) => {
        const { apiConfig: { keyProps }, queryClient } = apiMachine.context;
        try {
            await deletePaymentMethod.mutateAsync(paymentMethod);
            // proactively delete the payment method from UI after success DELETE call to prevent a user from deleting the same payment method again
            const paymentMethodsFromCache = queryClient.getQueryData(paymentMethodsCacheKey(keyProps));
            const updatedPaymentMethods = paymentMethodsFromCache === null || paymentMethodsFromCache === void 0 ? void 0 : paymentMethodsFromCache.paymentMethods.filter((eachPaymentMethod) => eachPaymentMethod.bankRoutingNumber !==
                paymentMethod.bankRoutingNumber);
            queryClient.setQueryData(paymentMethodsCacheKey(keyProps), {
                paymentMethods: updatedPaymentMethods
            });
            queryClient.invalidateQueries(paymentMethodsCacheKey(keyProps));
            sendToPaymentMethodMachine({
                type: 'FETCH_PAYMENT_METHODS'
            });
        }
        catch (error) {
            const errorResponse = error === null || error === void 0 ? void 0 : error.response;
            if (errorResponse && errorResponse.status) {
                const validationError = errorResponse.status === VALIDATION_ERROR_CODE;
                const { businessErrors } = await errorResponse.json();
                const [businessError] = businessErrors || [];
                const deletePaymentMethodError = isPaymentMethodErrorCode(businessError === null || businessError === void 0 ? void 0 : businessError.code);
                if (validationError && deletePaymentMethodError) {
                    const message = getPaymentMethodErrorMessage(businessError.code);
                    triggerAnalyticsEvent({
                        pageFunction: PAGE_FUNCTION.MAKE_PAYMENT.PAYMENTS,
                        pageSubFunction: SUB_FUNCTION.MANAGE_PAYMENT_METHOD.REMOVE_PAYMENT_METHOD,
                        pageName: PAGE_NAME.MANAGE_PAYMENT_METHOD.ERROR,
                        errorMessage: businessError === null || businessError === void 0 ? void 0 : businessError.message
                    });
                    setErrorMessage(message);
                    return;
                }
            }
            const systemError = t('errors.paymentMethodError');
            triggerAnalyticsEvent({
                pageFunction: PAGE_FUNCTION.MAKE_PAYMENT.PAYMENTS,
                pageSubFunction: SUB_FUNCTION.MANAGE_PAYMENT_METHOD.REMOVE_PAYMENT_METHOD,
                pageName: PAGE_NAME.MANAGE_PAYMENT_METHOD.ERROR,
                errorMessage: systemError
            });
            setErrorMessage(systemError);
        }
    };
    return (_jsxs(_Fragment, { children: [_jsxs(PaymentMethodsWrapper, Object.assign({ "data-cy": "manage-payment-method-page" }, { children: [windowWidth.isLessThanTablet ? (_jsx(PaymentMethodsMobile, { buttonRefs: buttonRefs, errorMessage: errorMessage, expandedId: expandedId, handleClosePrompt: handleClosePrompt, handleConfirmDeletePaymentMethod: handleConfirmDeletePaymentMethod, handleDeleteButton: handleDeleteButton, paymentMethods: paymentMethods })) : (_jsx(PaymentMethodsDesktop, { buttonRefs: buttonRefs, errorMessage: errorMessage, expandedId: expandedId, handleClosePrompt: handleClosePrompt, handleConfirmDeletePaymentMethod: handleConfirmDeletePaymentMethod, handleDeleteButton: handleDeleteButton, paymentMethods: paymentMethods })), _jsx(AddPaymentMethodButtonWrapper, Object.assign({ all: "large" }, { children: _jsxs(AddPaymentMethodButton, Object.assign({ buttonType: "text", "data-analytics": DATA_ANALYTICS.CLICK_TRACKING_EVENT, "data-cy": "add-payment-button", "data-reason": DATA_REASON.MANAGE_PAYMENT_METHODS.ADD_PAYMENT_METHOD_BODY, "data-testid": "add-payment-button", "data-type": DATA_TYPE.MAKE_A_PAYMENT, onClick: handleClickAddPaymentMethod }, { children: [_jsx(AddPaymentMethodIcon, {}), _jsx(Inline, { size: "small" }), t('makePayment.addPaymentMethod')] })) }))] })), _jsx(FooterContainer, { children: _jsx(ModalFooter, { dataPrimaryObject: DATA_OBJECT.BUTTON, dataPrimaryReason: DATA_REASON.MANAGE_PAYMENT_METHODS.ADD_PAYMENT_METHOD_FOOTER, dataSecondaryObject: DATA_OBJECT.BUTTON, dataSecondaryReason: DATA_REASON.MANAGE_PAYMENT_METHODS.ADD_PAYMENT_METHOD_CANCEL, dataType: DATA_TYPE.MAKE_A_PAYMENT, handlePrimaryButton: handleSubmit, handleSecondaryButton: handleCancel, primaryText: t('makePayment.addPaymentMethod'), secondaryButtonType: "ghost", secondaryText: t('button.cancel') }) })] }));
};
